/**
 * Keeps the horizontal product card's price in step with its inline variant
 * select (SPEC §6.1, QA-34, Figma 697:46086).
 *
 * The select is a real `name="id"` control inside the product form, so the right
 * variant is submitted whether or not this module ever loads. All this adds is
 * the visible price — the half a customer checks before clicking, and the half
 * that stays wrong for the whole session if nothing updates it.
 *
 * Deliberately not a `Component`: Horizon scopes `ref` to the nearest component
 * ancestor, and the select's nearest ancestor is `product-form-component`, which
 * owns it. Reaching across that boundary with refs is not possible and faking it
 * would mean shadowing a vendor component. Two `querySelector` calls on connect
 * are cheaper than that trade.
 *
 * Prices come from each option's `data-price`, already formatted by Liquid's
 * `money` filter. Re-formatting in JavaScript would mean re-implementing the
 * store's currency format, and getting Romanian's "1.234,56 lei" wrong is a more
 * likely outcome than getting it right.
 */
class AdmiraCardVariantSelect extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #select = null;

  /** @type {HTMLElement | null} */
  #price = null;

  #onChange = () => this.#update();

  connectedCallback() {
    this.#select = this.querySelector('select[name="id"]');
    this.#price = this.querySelector('.admira-product-card__amount');

    // No select means the card rendered its single-variant or link action
    // instead. Nothing to do, and nothing to warn about.
    if (!this.#select || !this.#price) return;

    this.#select.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.#select?.removeEventListener('change', this.#onChange);
  }

  #update() {
    const formatted = this.#select?.selectedOptions[0]?.dataset.price;
    if (!formatted || !this.#price) return;

    // The card shows "de la <lowest>" at rest. Once a variant is chosen the
    // range no longer applies, so the prefix goes with it — and the amount
    // keeps its own span, which is what sets it a step above the prefix.
    const value = document.createElement('span');
    value.className = 'admira-product-card__amount-value';
    value.textContent = formatted;
    this.#price.replaceChildren(value);
  }
}

if (!customElements.get('admira-card-variant-select')) {
  customElements.define('admira-card-variant-select', AdmiraCardVariantSelect);
}
